import React, { useCallback, useContext, useMemo } from 'react';
import { noop } from '@splunk/dashboard-utils';
import styled from 'styled-components';
import Cross from '@splunk/react-icons/Cross';
import RemoteIcon from '@splunk/visualizations-shared/RemoteIcon';
import { variables } from '@splunk/themes';
import ScreenReaderContent from '@splunk/react-ui/ScreenReaderContent';
import { _ } from '@splunk/ui-utils/i18n';
import { createDOMID } from '@splunk/ui-utils/id';
import type { ItemProps } from '../Gallery';
import { KeyboardNavigationContext } from '../GalleryKeyboardNavigation';

const customVariables = {
    transparentPatternEvenColor: variables.neutral400,
    transparentPatternOddColor: variables.neutral500,
};

const IconContainer = styled.li`
    position: relative;
    list-style: none;
`;

const IconButton = styled.button<Pick<ItemProps, 'selected'>>`
    background: transparent;
    border: transparent;
    padding: 0;
    font-family: inherit;
    font-size: inherit;
    width: 60px;
    height: 65px;
    margin: 0 5px;
    padding-top: 8px;
    border-radius: 3px;
    &:hover {
        cursor: pointer;
        background: ${variables.actionColorBackgroundSecondaryHover};
    }
`;
const RemoveButton = styled.button<Pick<ItemProps, 'selected'>>`
    display: flex;
    opacity: ${(props) => (props.selected ? '1' : '0')};
    justify-content: flex-end;
    background: transparent;
    border: 2px solid transparent;
    border-radius: 3px;
    outline: none;
    margin-left: auto;
    padding: 0;
    position: absolute;
    top: -3px;
    right: 2px;
    font-size: inherit;

    &:hover,
    &:focus {
        border-color: rgb(153, 200, 255);
        opacity: 1;
    }
`;

const IconText = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    color: ${variables.contentColorDefault};
`;

const Icon = styled.div`
    width: 40px;
    height: 40px;
    margin: 0px 10px 0px 10px;
    background-color: transparent;
    background-image:
        linear-gradient(
            45deg,
            ${customVariables.transparentPatternOddColor} 25%,
            transparent 25%,
            transparent 75%,
            ${customVariables.transparentPatternOddColor} 75%,
            ${customVariables.transparentPatternOddColor} 100%
        ),
        linear-gradient(
            45deg,
            ${customVariables.transparentPatternOddColor} 25%,
            ${customVariables.transparentPatternEvenColor} 25%,
            ${customVariables.transparentPatternEvenColor} 75%,
            ${customVariables.transparentPatternOddColor} 75%,
            ${customVariables.transparentPatternOddColor} 100%
        );
    background-size: 5px 5px;
    background-position:
        0 0,
        2.5px 2.5px;
    border-radius: 3px;
    display: flex;
    align-items: center;
`;

export type IconGalleryItemProps = ItemProps;

const IconGalleryItem = ({
    item,
    selected = false,
    onClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onRemove = noop,
}: IconGalleryItemProps) => {
    const { name, url } = item;
    const deleteIconA11yTextId = useMemo(() => createDOMID(), []);

    const handleClick = useCallback(() => onClick(item.url), [onClick, item]);
    const handleMouseEnter = useCallback(
        () => onMouseEnter(item),
        [onMouseEnter, item]
    );
    const handleMouseLeave = useCallback(
        () => onMouseLeave(item),
        [onMouseLeave, item]
    );
    const handleDelete = useCallback<
        React.MouseEventHandler<HTMLButtonElement>
    >(
        (evt) => {
            evt.stopPropagation();
            onRemove?.(item);
        },
        [onRemove, item]
    );

    const { onKeyDownHandler } = useContext(KeyboardNavigationContext);
    const hasRemoveButton = !!onRemove;
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLElement>) =>
            onKeyDownHandler(e, hasRemoveButton),
        [onKeyDownHandler, hasRemoveButton]
    );

    return (
        <IconContainer data-test="icon-container">
            <IconButton
                onKeyDown={handleKeyDown}
                data-test="icon-button"
                data-url={url}
                key={url}
                title={name}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                onBlur={handleMouseLeave}
                tabIndex={-1}
            >
                <Icon data-test="icon">
                    <RemoteIcon iconURL={url} width={30} height={30} alt="" />
                </Icon>
                <IconText data-test="icon-name">{name}</IconText>
            </IconButton>
            {onRemove && (
                <>
                    <ScreenReaderContent id={deleteIconA11yTextId}>
                        {_(`Delete ${name} icon`)}
                    </ScreenReaderContent>
                    <RemoveButton
                        selected={selected}
                        data-test="icon-close"
                        onClick={handleDelete}
                        onKeyDown={handleKeyDown}
                        tabIndex={-1}
                        aria-labelledby={deleteIconA11yTextId}
                    >
                        <Cross width="12px" height="12px" />
                    </RemoveButton>
                </>
            )}
        </IconContainer>
    );
};
export default IconGalleryItem;
